import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IdCard, CheckCircle2, XCircle, LogOut, Save, ShieldCheck, Award, Rocket, Check, KeyRound, Mail } from "lucide-react";
import api, { mediaSrc, avatarFor, ymd } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RecruiterProfileCard } from "@/components/RecruiterPanel";
import MobileAppCard from "@/components/MobileAppCard";
import AvatarCropper from "@/components/AvatarCropper";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow" style={{ letterSpacing: "0.14em" }}>{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
const input = "w-full rsp-card px-4 py-2.5 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)]";
const COMPLETE_FIELDS = ["mos", "rank", "phone", "contact_email", "photo_url", "ship_date", "bct_location"];

function RecruitProfile() {
  const { user, setUser } = useAuth();
  const [mos, setMos] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState(null);

  const uploadBlob = async (blob) => {
    setCropFile(null);
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", blob, "avatar.jpg");
      const { data } = await api.post("/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: u } = await api.post("/me/profile", { photo_url: data.url });
      setUser(u);
      setForm((p) => ({ ...p, photo_url: data.url }));
      toast.success("Photo updated.");
    } catch (err) { toast.error("Upload failed."); } finally { setUploading(false); }
  };

  useEffect(() => {
    api.get("/mos").then((r) => setMos(r.data)).catch(() => {});
    api.get("/recruiters").then((r) => setRecruiters(r.data)).catch(() => {});
    setForm({
      rank: user.rank || "", mos: user.mos || "", phone: user.phone || "",
      contact_email: user.contact_email || "", photo_url: user.photo_url || "",
      bct_location: user.bct_location || "", recruiter: user.recruiter || "",
      gender: user.gender || "",
    });
  }, [user]);

  const comp = Math.round((COMPLETE_FIELDS.filter((f) => user?.[f]).length / COMPLETE_FIELDS.length) * 100);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/profile", form);
      setUser(data);
      toast.success("Profile updated.");
    } catch (e) { toast.error("Could not save."); } finally { setBusy(false); }
  };

  const toggleCac = async () => {
    try {
      const { data } = await api.post("/me/profile", { has_cac: !user.has_cac });
      setUser(data);
      toast.success(`CAC marked ${!user.has_cac ? "issued" : "pending"}.`);
    } catch (e) { toast.error("Could not update CAC."); }
  };

  const saveShip = async (val) => {
    try {
      const { data } = await api.post("/me/ship-date", { ship_date: val || null });
      setUser(data);
      toast.success("Ship date saved.");
    } catch (e) { toast.error("Could not save ship date."); }
  };

  return (
    <div className="space-y-6">
      <div className="rsp-card p-4 flex items-center gap-3" data-testid="cac-status">
        <IdCard className="gold" size={24} />
        <div className="flex-1">
          <div className="font-head uppercase tracking-wide">CAC Status</div>
          <div className="text-[var(--rsp-gray)] text-sm">Common Access Card issuance</div>
        </div>
        {user.has_cac ? (
          <span className="flex items-center gap-1 text-emerald-400 font-head uppercase text-sm"><CheckCircle2 size={16} /> Issued</span>
        ) : (
          <span className="flex items-center gap-1 text-[var(--rsp-muted)] font-head uppercase text-sm"><XCircle size={16} /> Pending</span>
        )}
        <button onClick={toggleCac} data-testid="cac-toggle"
          className="ml-2 text-xs px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)]">
          {user.has_cac ? "Mark pending" : "Mark issued"}
        </button>
      </div>

      {user.has_cac && (
        <div className="rsp-card p-4 flex items-center gap-3 border-emerald-500/40" style={{ background: "rgba(16,185,129,0.06)" }} data-testid="cac-confirmed">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center"><CheckCircle2 className="text-emerald-400" size={22} /></div>
          <div>
            <div className="font-head uppercase tracking-wide text-emerald-400">CAC Confirmed</div>
            <div className="text-[var(--rsp-gray)] text-sm">Your Common Access Card is on file.</div>
          </div>
        </div>
      )}

      <div className="rsp-card p-4" data-testid="profile-completeness">
        <div className="font-head uppercase tracking-wide mb-2">Profile Completeness · {comp}%</div>
        <div className="h-2 rounded-full bg-[var(--rsp-bg-2)] overflow-hidden">
          <div className="h-full bg-[var(--rsp-gold)]" style={{ width: `${comp}%` }} />
        </div>
      </div>

      <div className="rsp-card p-4 space-y-4">
        <div className="flex items-center gap-2"><Rocket className="gold" size={18} /><span className="font-head uppercase tracking-wide">Ship Date</span></div>
        <input type="date" data-testid="ship-date-input" defaultValue={user.ship_date ? user.ship_date.slice(0, 10) : ""}
          onChange={(e) => saveShip(e.target.value)} className={input} />
        <div>
          <span className="eyebrow" style={{ letterSpacing: "0.14em" }}>AIT End Date</span>
          <div className="mt-1.5 rsp-card px-4 py-2.5 text-sm" data-testid="ait-end-date">
            {user.ait_end_date
              ? ymd(user.ait_end_date)
              : <span className="text-[var(--rsp-muted)]">Not set yet — your recruiter or cadre will add this</span>}
          </div>
        </div>
        <Field label="BCT Location">
          <select className={input} data-testid="bct-input" value={form.bct_location || ""} onChange={(e) => set("bct_location", e.target.value)}>
            <option value="">Select training location</option>
            <option value="Fort Jackson, SC">Fort Jackson, SC</option>
            <option value="Fort Moore, GA">Fort Moore, GA</option>
            <option value="Fort Leonard Wood, MO">Fort Leonard Wood, MO</option>
            <option value="Fort Sill, OK">Fort Sill, OK</option>
          </select>
        </Field>
      </div>

      <div className="rsp-card p-4 space-y-4" data-testid="recruit-details">
        <div className="font-head uppercase tracking-wide flex items-center gap-2"><Award className="gold" size={18} /> Recruit Details</div>
        <div>
          <span className="eyebrow" style={{ letterSpacing: "0.14em" }}>Gender</span>
          <div className="mt-1.5 grid grid-cols-2 gap-3">
            {[["male", "Male"], ["female", "Female"]].map(([val, label]) => {
              const on = form.gender === val;
              return (
                <button key={val} type="button" onClick={() => set("gender", on ? "" : val)}
                  data-testid={`gender-${val}`}
                  className={`flex items-center gap-2 rsp-card px-4 py-2.5 transition-colors ${on ? "border-[var(--rsp-gold)] text-[var(--rsp-cream)]" : "text-[var(--rsp-gray)] hover:border-[var(--rsp-gold)]/50"}`}>
                  <span className={`w-5 h-5 rounded flex items-center justify-center border ${on ? "bg-[var(--rsp-gold)] border-[var(--rsp-gold)]" : "border-[var(--rsp-border)]"}`}>
                    {on && <Check size={14} className="text-black" />}
                  </span>
                  <span className="font-head uppercase tracking-wide text-sm">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Rank"><input className={input} value={form.rank || ""} onChange={(e) => set("rank", e.target.value)} placeholder="E-1 PVT" data-testid="profile-rank" /></Field>
          <Field label="MOS">
            <select className={input} value={form.mos || ""} onChange={(e) => set("mos", e.target.value)} data-testid="profile-mos">
              <option value="">Select MOS</option>
              {mos.map((m) => <option key={m.code} value={m.code}>{m.code} — {m.title}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Recruiter">
          <select className={input} value={form.recruiter || ""} onChange={(e) => set("recruiter", e.target.value)} data-testid="profile-recruiter">
            <option value="">Select Recruiter</option>
            {recruiters.map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone"><input className={input} value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="profile-phone" /></Field>
          <Field label="Contact Email"><input className={input} value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="profile-contact-email" /></Field>
        </div>
        <Field label="Profile Photo">
          <div className="flex items-center gap-3">
            <img src={form.photo_url ? mediaSrc(form.photo_url) : avatarFor({ gender: form.gender })} alt="avatar"
              className="w-14 h-14 rounded-lg object-cover border border-[var(--rsp-border)]" data-testid="recruit-avatar" />
            <label className="btn-gold px-4 py-2.5 cursor-pointer text-sm" data-testid="profile-photo-upload-label">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) setCropFile(e.target.files[0]); e.target.value = ""; }} data-testid="profile-photo-upload" />
              {uploading ? "Uploading…" : "Upload from device"}
            </label>
          </div>
          {cropFile && <AvatarCropper file={cropFile} onCancel={() => setCropFile(null)} onConfirm={uploadBlob} />}
        </Field>
        <button onClick={save} disabled={busy} className="btn-gold w-full py-3 flex items-center justify-center gap-2" data-testid="profile-save">
          <Save size={16} /> {busy ? "Saving…" : "Save Profile"}
        </button>
      </div>
    </div>
  );
}

function PasswordCard() {
  const { user, refresh } = useAuth();
  const has = !!user.has_password;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (form.next.length < 8) return toast.error("Password must be at least 8 characters.");
    if (form.next !== form.confirm) return toast.error("Passwords do not match.");
    if (has && !form.current) return toast.error("Enter your current password.");
    setBusy(true);
    try {
      await api.post("/auth/set-password", { current_password: has ? form.current : undefined, new_password: form.next });
      toast.success(has ? "Password changed." : "Password created — you can now sign in with email.");
      setForm({ current: "", next: "", confirm: "" });
      setOpen(false);
      await refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update password.");
    } finally { setBusy(false); }
  };

  const pin = "w-full rsp-card px-3 py-2.5 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)] text-sm";
  return (
    <div className="rsp-card p-5 mt-4" data-testid="password-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound className="gold" size={20} />
          <div>
            <div className="font-head uppercase tracking-wide">{has ? "Change Password" : "Create Password"}</div>
            <div className="text-[var(--rsp-gray)] text-xs">{has ? "Update your email-login password" : "Add a password to sign in with email (not just Google)"}</div>
          </div>
        </div>
        <button onClick={() => setOpen((v) => !v)} data-testid="password-toggle"
          className="text-sm px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-gold)] hover:border-[var(--rsp-gold)]/60 transition-colors">
          {open ? "Cancel" : has ? "Change" : "Create"}
        </button>
      </div>
      {open && (
        <div className="space-y-3 mt-4">
          {has && (
            <input className={pin} type="password" placeholder="Current password" value={form.current} onChange={(e) => set("current", e.target.value)} data-testid="password-current" />
          )}
          <input className={pin} type="password" placeholder="New password (min 8 characters)" value={form.next} onChange={(e) => set("next", e.target.value)} data-testid="password-new" />
          <input className={pin} type="password" placeholder="Confirm new password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} data-testid="password-confirm" />
          <button onClick={submit} disabled={busy} className="btn-gold w-full py-2.5 disabled:opacity-60" data-testid="password-save">
            {busy ? "Saving…" : has ? "Update Password" : "Create Password"}
          </button>
        </div>
      )}
    </div>
  );
}

function AdminContactCard() {
  const { user, setUser } = useAuth();
  const [email, setEmail] = useState(user.secondary_email || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/profile", { secondary_email: email });
      setUser(data);
      toast.success("Contact email saved.");
    } catch (e) { toast.error("Could not save."); } finally { setBusy(false); }
  };
  return (
    <div className="rsp-card p-5 space-y-3" data-testid="admin-contact-card">
      <div className="flex items-center gap-2"><Mail className="gold" size={18} /><span className="font-head uppercase tracking-wide">Contact Email</span></div>
      <div className="text-[var(--rsp-gray)] text-xs">Recruits use this address when they tap Email on your leadership card. It won't change your login email.</div>
      <input className={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Secondary contact email" data-testid="admin-secondary-email" />
      <button onClick={save} disabled={busy} className="btn-gold w-full py-2.5 flex items-center justify-center gap-2" data-testid="admin-secondary-save">
        <Save size={16} /> {busy ? "Saving…" : "Save Contact Email"}
      </button>
    </div>
  );
}


export default function Profile() {
  const { user, effectiveRole, isAdmin, viewAs, setViewAs, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div data-testid="profile-page">
      <div className="flex items-center gap-4 mb-6">
        <img src={mediaSrc(user.photo_url) || user.picture || avatarFor(user)} alt={user.name}
          className="w-16 h-16 rounded-xl object-cover border border-[var(--rsp-border)]" data-testid="profile-header-avatar" />
        <div>
          <h1 className="heading text-3xl leading-none">{user.name}</h1>
          <div className="text-[var(--rsp-gray)] text-sm">{user.email}</div>
        </div>
      </div>

      <MobileAppCard />

      {effectiveRole === "recruit" && <RecruitProfile />}
      {effectiveRole === "recruiter" && <RecruiterProfileCard />}
      {effectiveRole === "family" && (
        <div className="rsp-card p-6 text-[var(--rsp-gray)]" data-testid="family-profile">
          <ShieldCheck className="gold mb-2" /> You have read-only family access. Linked recruit: {user.recruit_email || "—"}.
        </div>
      )}
      {effectiveRole === "admin" && (
        <div className="space-y-4" data-testid="admin-profile">
          <div className="rsp-card p-5">
            <div className="flex items-center gap-2 mb-1"><ShieldCheck className="gold" size={20} /><span className="font-head uppercase tracking-wide text-lg">Administrator</span></div>
            <div className="text-[var(--rsp-gray)]">Title: <span className="gold font-head uppercase">{user.admin_title || "Cadre"}</span></div>
          </div>
          <AdminContactCard />
          <div className="rsp-card p-5" data-testid="view-as-section">
            <div className="eyebrow mb-3" style={{ letterSpacing: "0.16em" }}>View As</div>
            <div className="flex flex-wrap gap-2">
              {["admin", "recruit", "recruiter", "family"].map((r) => (
                <button key={r} data-testid={`view-as-${r}`} onClick={() => setViewAs(r === "admin" ? null : r)}
                  className={`px-4 py-2 rounded-full text-sm font-head uppercase tracking-wide border transition-colors ${
                    (viewAs || "admin") === r ? "bg-[var(--rsp-gold)] text-black border-[var(--rsp-gold)]" : "text-[var(--rsp-gray)] border-[var(--rsp-border)] hover:text-[var(--rsp-cream)]"}`}>
                  {r === "admin" ? "Admin" : r === "recruit" ? "Recruit" : r === "recruiter" ? "Recruiter" : "Family"}
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => navigate("/admin")} data-testid="open-admin-cms" className="btn-gold w-full py-3">Open Admin CMS</button>
        </div>
      )}

      <PasswordCard />

      <button onClick={() => navigate("/mos")} data-testid="open-mos" className="w-full rsp-card p-4 mt-4 flex items-center gap-3 hover:border-[var(--rsp-gold)]/60">
        <Award className="gold" size={20} /> <span className="font-head uppercase tracking-wide">My MOS &amp; Base Pay</span>
      </button>

      <button onClick={logout} data-testid="logout-btn"
        className="w-full mt-6 py-3 rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)] flex items-center justify-center gap-2">
        <LogOut size={16} /> Sign out
      </button>
    </div>
  );
}
